import instantsearch from 'instantsearch.js';
import { algoliasearch } from 'algoliasearch';
import {
	configure,
	hits,
	pagination,
	panel,
	refinementList,
	searchBox,
	stats,
	menuSelect,
} from 'instantsearch.js/es/widgets';
import { history } from 'instantsearch.js/es/lib/routers';
import SourcePoint from '../gdpr/sourcepoint';

class InstantSearch {
	constructor() {
		this.algolia = window.algolia;
		this.searchBoxClass = 'algolia-search-box';
		this.maybeInit();
	}

	// The Algolia plugin indexes the WordPress timezone instead of GMT timestamps.
	// We have to provide a timezone to DateTimeFormat because else it uses a wrong default.
	formatTimestamp(timestamp) {
		const date = new Date(timestamp * 1000);
		const timeFormatter = new Intl.DateTimeFormat('de-DE', {
			hour: '2-digit',
			minute: '2-digit',
			timeZone: 'GMT',
		});
		const dateFormatter = new Intl.DateTimeFormat('de-DE', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
			timeZone: 'GMT',
		});

		return `${timeFormatter.format(date)}, ${dateFormatter.format(date)}`;
	}

	maybeInit() {
		if (!SourcePoint.vendorHasGrant('algolia')) {
			return;
		}

		this.init();
	}

	init() {
		if (!this.isSearchPage()) {
			return;
		}

		// Remove the algolia placeholder if it is still present
		document.querySelector('.sourcepoint__algolia-placeholder')?.remove();

		if (!this.hasValidIndex()) {
			this.showIndexAlert();
			return;
		}

		this.setupSearch();
	}

	isSearchPage() {
		return !!document.getElementById(this.searchBoxClass);
	}

	hasValidIndex() {
		return this.algolia.indices.searchable_posts !== undefined;
	}

	showIndexAlert() {
		alert(this.getI18nString('no_index_alert'));
	}

	getIndexName() {
		return this.algolia.indices.searchable_posts.name;
	}

	setupSearch() {
		// Define the URL parameters that should be used for filtering. Map where key is the url param and value is the menu name
		const filterUrlParams = {
			bi_post_source: 'bi_post_source',
			bi_post_access: 'bi_post_access',
		};

		this.search = instantsearch({
			indexName: this.getIndexName(),
			searchClient: algoliasearch(
				this.algolia.application_id,
				this.algolia.search_api_key,
			),
			numberLocale: this.getSetting('locale'),
			routing: {
				router: history({ writeDelay: 1000 }),

				// Add custom URL state mapping to match WordPress search query behavior
				stateMapping: {
					stateToRoute: (indexUiState) => {
						const currentState =
							indexUiState[this.algolia.indices.searchable_posts.name];
						const query = currentState.query || '';

						let route = {
							s: encodeURIComponent(query),
							page: currentState.page,
						};

						if (currentState.menu) {
							for (const [filterUrlParam, menuItemName] of Object.entries(
								filterUrlParams,
							)) {
								if (currentState.menu[menuItemName]) {
									route[filterUrlParam] = encodeURIComponent(
										currentState.menu[menuItemName],
									);
								}
							}
						}

						return route;
					},
					routeToState: (routeState) => {
						const indexUiState = {};
						let menus = {};

						for (const [filterUrlParam, menuItemName] of Object.entries(
							filterUrlParams,
						)) {
							if (routeState[filterUrlParam]) {
								menus[menuItemName] = decodeURIComponent(
									routeState[filterUrlParam],
								);
							}
						}

						indexUiState[this.algolia.indices.searchable_posts.name] = {
							query: routeState.s,
							page: routeState.page,
							menu: menus,
						};
						return indexUiState;
					},
				},
			},
		});

		this.manageLoadingState();
		this.addWidgets();
		this.startSearch();
	}

	manageLoadingState() {
		this.search.on('render', () => {
			this.modifyLoader(this.search.status);
			this.modifyDefaultTitle(this.search.status);
			this.modifyDefaultRefinementsTitle(this.search.status);
		});
	}

	// Modify the visibility of the loading indicator based on the search status.
	modifyLoader() {
		const container = document.querySelector('#algolia-loading');
		if (this.search.status === 'loading' || this.search.status === 'stalled') {
			container.style.display = 'block';
		} else {
			container.style.display = 'none';
		}
	}

	// Modify the visibility of the title based on the search status.
	modifyDefaultTitle() {
		const defaultTitle = document.querySelector(
			'#algolia-stats-title .search_stats_title_default',
		);
		defaultTitle.style.display = 'none';
	}

	// Modify the visibility of the default refinement text based on the search status.
	modifyDefaultRefinementsTitle() {
		const defaultRefinementsTitles = document.querySelectorAll(
			'.search_refinements_title_default',
		);
		defaultRefinementsTitles.forEach((title) => {
			title.style.display = 'none';
		});
	}

	startSearch() {
		this.search.start();
	}

	addWidgets() {
		// Setup widgets
		const widgets = [
			this.configureWidgets(),
			this.getSearchBoxWidget(),
			this.getSearchResultsWidget(),
			this.getPaginationWidget(),
			this.getStatsWidget(),
			this.getSourceWidget(),
			this.getAccessWidget(),
		];

		// Add widgets to the search instance
		this.search.addWidgets(widgets);
	}

	getStatsWidget() {
		return stats({
			container: '#algolia-stats-title',
			cssClasses: {
				root: 'search_stats_title',
				text: 'tw-text-sm tw-text-black tw-font-normal tw-font-lab-grotesque',
			},
			templates: {
				text: (data, { html }) => {
					const query = data.query;
					const amount = data.hasManyResults
						? `${data.nbHits} ${this.getI18nString('results')}`
						: data.hasOneResult
							? `1 ${this.getI18nString('result')}`
							: this.getI18nString('no_results');
					return html`${this.formatI18nString('search_results_for_query', {
						amount,
						query,
					})}`;
				},
			},
		});
	}

	// Configure widgets
	configureWidgets() {
		return configure({
			hitsPerPage: 10,
		});
	}

	// Add search box widget
	getSearchBoxWidget() {
		return searchBox({
			container: '#algolia-search-box',
			placeholder: this.getI18nString('search_placeholder'),
			autofocus: true,
			showReset: true,
			showSubmit: true,
			showLoadingIndicator: false,
			cssClasses: {
				form: 'tw-relative sm:tw-gap-6 before:tw-content-none after:tw-content-none tw-mb-0 tw-flex tw-gap-[9px] tw-justify-stretch',
				input:
					'tw-order-1 tw-border sm:tw-col-span-2 tw-outline-offset-2 !tw-border-slate tw-w-full !tw-font-normal !tw-font-lab-grotesque tw-rounded-[4px] !tw-px-4 tw-bg-white placeholder:tw-text-[#757575] placeholder:!tw-font-normal !tw-text-base !tw-h-[52px] !tw-mb-0',
				submit:
					'[text-shadow:_none] active:tw-shadow-none tw-max-w-[200px] md:tw-max-w-[283px] sm:tw-w-full tw-order-3 tw-font-bold tw-shadow-none tw-drop-shadow-none tw-uppercase hocus:!tw-text-obsidian hocus:tw-bg-none hocus:tw-border-slate tw-justify-center hocus:tw-border-obsidian hocus:!tw-bg-white tw-size-[52px] sm:tw-size-auto tw-rounded-[4px] tw-aspect-square sm:tw-aspect-auto svg:tw-size-4 !tw-p-0 tw-grid tw-place-items-center tw-bg-none tw-transition-colors tw-cursor-pointer tw-bg-cobalt tw-border-cobalt tw-text-white sm:tw-flex sm:tw-gap-2 sm:tw-px-6',
				reset:
					'tw-top-[1px] tw-right-[62px] sm:tw-right-[225px] md:tw-right-[309px] tw-order-2 tw-bg-white tw-cursor-pointer tw-text-obsidian svg:tw-size-[13px] tw-p-0 hocus:tw-bg-none hocus:tw-text-gray-700 tw-transition-colors tw-absolute tw-right-[1px] tw-top-[1px] tw-grid tw-size-[50px] tw-bg-none tw-border-none tw-place-items-center active:tw-bg-none active:tw-shadow-none active:tw-bg-white',
			},
			templates: {
				submit: () => {
					return `<span class="tw-hidden sm:tw-inline">${this.getI18nString('submit_button')}</span><svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M6.84872 0.618103C3.06628 0.618103 0 3.68438 0 7.46683C0 11.2493 3.06628 14.3155 6.84872 14.3155C8.49932 14.3155 10.0135 13.7316 11.1961 12.7591L14.6965 16.2595C14.8596 16.4226 15.124 16.4226 15.2871 16.2595L15.8777 15.669C16.0408 15.5059 16.0408 15.2415 15.8777 15.0784L12.3484 11.5491C13.1959 10.4091 13.6974 8.99652 13.6974 7.46683C13.6974 3.68438 10.6312 0.618103 6.84872 0.618103ZM1.67042 7.46683C1.67042 4.60693 3.98883 2.28852 6.84872 2.28852C9.70862 2.28852 12.027 4.60693 12.027 7.46683C12.027 10.3267 9.70862 12.6451 6.84872 12.6451C3.98883 12.6451 1.67042 10.3267 1.67042 7.46683Z" fill="currentColor"/>
</svg>`;
				},
			},
		});
	}

	// Add search results widget
	getSearchResultsWidget() {
		return hits({
			container: '#algolia-hits',
			templates: {
				empty: (results, { html }) => {
					const query = `<q>${results.query}</q>`;
					return html`${this.formatI18nString('no_results_for_query', {
						query,
					})}`;
				},
				item: (hit) => {
					hit.formatted_date = this.formatTimestamp(hit.post_date);
					hit.sr_text_date = this.getI18nString('sr_date');
					hit.sr_text_source = this.getI18nString('sr_source');
					hit.sr_text_access = this.getI18nString('sr_premium_post');
					hit.sr_text_category = this.getI18nString('sr_category');
					hit.post_access = this.getI18nString('post_access');
					hit.post_source = this.getI18nString('post_source');

					return wp.template('search-result-item')(hit);
				},
			},
			cssClasses: {
				root: 'search_hits',
				list: 'search_hits_list',
			},
		});
	}

	// Pagination widget
	getPaginationWidget() {
		const paginationWithPanel = panel({
			hidden: ({ results }) => results.nbPages === 1,
		})(pagination);

		return paginationWithPanel({
			container: '#algolia-pagination',
			padding: 2,
			showFirst: false,
			showLast: false,
			cssClasses: {
				list: 'tw-list-none tw-flex-wrap tw-flex tw-items-center tw-gap-2 tw-justify-center tw-m-0',
				item: 'tw-font-lab-grotesque-black tw-font-bold tw-uppercase tw-m-0',
				link: 'tw-text-obsidian tw-underline-fade-in hocus:tw-text-obsidian tw-p-2 ',
				previousPageItem:
					'tw-mr-2 tw-size-9 [&_>_*]:tw-p-0 [&_>_*]:tw-leading-none tw-bg-white tw-border-solid tw-rounded-full tw-border tw-border-slate tw-cursor-pointer tw-transition-colors tw-grid tw-place-items-center tw-shadow-none hocus:tw-shadow-none hocus:tw-border-cobalt',
				nextPageItem:
					'tw-ml-2 tw-size-9 [&_>_*]:tw-p-0 [&_>_*]:tw-leading-none tw-bg-white tw-border-solid tw-rounded-full tw-border tw-border-slate tw-cursor-pointer tw-transition-colors tw-grid tw-place-items-center tw-shadow-none hocus:tw-shadow-none hocus:tw-border-cobalt',
			},
			templates: {
				previous:
					() => `<svg class="tw-size-3 tw-translate-y-[1px] -tw-translate-x-[1px]" width="9" height="14" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M8.20125 0.292893C8.59177 0.683417 8.59177 1.31658 8.20125 1.70711L2.90835 7L8.20125 12.2929C8.59177 12.6834 8.59177 13.3166 8.20125 13.7071C7.81072 14.0976 7.17756 14.0976 6.78703 13.7071L0.787034 7.70711C0.39651 7.31658 0.39651 6.68342 0.787034 6.29289L6.78703 0.292893C7.17756 -0.0976311 7.81072 -0.0976311 8.20125 0.292893Z" fill="#111111"/>
					</svg>`,
				next: () => `<svg class="tw-size-3 tw-rotate-180 tw-translate-y-[1px] tw-translate-x-[1px]" width="9" height="14" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M8.20125 0.292893C8.59177 0.683417 8.59177 1.31658 8.20125 1.70711L2.90835 7L8.20125 12.2929C8.59177 12.6834 8.59177 13.3166 8.20125 13.7071C7.81072 14.0976 7.17756 14.0976 6.78703 13.7071L0.787034 7.70711C0.39651 7.31658 0.39651 6.68342 0.787034 6.29289L6.78703 0.292893C7.17756 -0.0976311 7.81072 -0.0976311 8.20125 0.292893Z" fill="#111111"/>
					</svg>`,
			},
		});
	}

	getSourceWidget() {
		return menuSelect({
			container: '#facet-source',
			attribute: 'bi_post_source',
			sortBy: ['isRefined:desc', 'count:desc', 'name:asc'],
			limit: 10,
			cssClasses: {
				root: 'tw-select-chevron tw-relative',
				select:
					'tw-cursor-pointer tw-text-obsidian tw-pr-10 tw-h-[44px] tw-font-bold tw-uppercase tw-bg-white tw-text-xs tw-appearance-none tw-rounded-[4px] tw-px-4 tw-font-lab-grotesque-black tw-w-full tw-border-slate',
			},
			templates: {
				defaultOption: (data, { html }) => {
					return html`<span>${this.getI18nString('all_brands')}</span>`;
				},
				item(item, { html }) {
					return html`<span>${item.label}</span>`;
				},
			},
		});
	}

	getAccessWidget() {
		return menuSelect({
			container: '#facet-access',
			attribute: 'bi_post_access',
			sortBy: ['isRefined:desc', 'count:desc', 'name:asc'],
			limit: 10,
			cssClasses: {
				root: 'tw-select-chevron tw-relative',
				select:
					'tw-cursor-pointer tw-text-obsidian tw-pr-10 tw-h-[44px] tw-font-bold tw-uppercase tw-bg-white tw-text-xs tw-appearance-none tw-rounded-[4px] tw-px-4 tw-font-lab-grotesque-black tw-w-full tw-border-slate',
			},
			templates: {
				defaultOption: (data, { html }) => {
					return html`<span>${this.getI18nString('all_articles')}</span>`;
				},
				item(item, { html }) {
					return html`<span>${item.label}</span>`;
				},
			},
		});
	}

	// Refinements widget
	getRefinementLists() {
		const widgetCategory = this.createRefinementWidget(
			'taxonomies.category',
			'categories',
		);
		const widgetTag = this.createRefinementWidget(
			'taxonomies.post_tag',
			'tags',
		);
		return [widgetCategory, widgetTag];
	}

	createRefinementWidget(indexAttribute, elementIdSuffix, options = {}) {
		const defaultOptions = {
			container: `#facet-refinements-${elementIdSuffix}`,
			attribute: indexAttribute,
			operator: 'and',
			limit: 15,
			sortBy: ['isRefined:desc', 'count:desc', 'name:asc'],
			showMore: true,
			showMoreLimit: 30,
			cssClasses: {
				root: 'facets',
				list: 'facets-list',
				item: 'facets-item',
				count: 'facets-count',
				selectedItem: 'facets-selected-item',
			},
		};

		return refinementList({ ...defaultOptions, ...options });
	}

	// Retrieves a setting value by key.
	getSetting(key) {
		return window.algolia_search[key] || '';
	}

	// Gets an internationalized string by key.
	getI18nString(key) {
		const strings = this.getSetting('i18n') || {};
		return strings[key] || key;
	}

	// Formats an internationalized string with passed parameters.
	formatI18nString(key, params) {
		const string = this.getI18nString(key);
		return string.replace(/{(\w+)}/g, (match, key) => {
			return Object.prototype.hasOwnProperty.call(params, key)
				? String(params[key])
				: match;
		});
	}
}

export default InstantSearch;
